import React, { useState } from 'react';
import axios from '../../config/axios';
import { useApi } from '../lib/api';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip, Button } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { confirmDialog } from '../lib/confirm';
import { notifyError, notifySuccess } from '../lib/notify';

const MyVarieties = () => {
  const { data, loading, error, refetch } = useApi('/api/variety-reviews');
  const reviews = Array.isArray(data?.data) ? data.data : [];

  const [plant_name, setPlantName] = useState('');
  const [variety, setVariety] = useState('');
  const [season_year, setSeasonYear] = useState('');
  const [yield_rating, setYieldRating] = useState(null);
  const [taste_rating, setTasteRating] = useState(null);
  const [resistance_rating, setResistanceRating] = useState(null);
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState(null);

  const currentYear = new Date().getFullYear();

  const resetForm = () => {
    setPlantName('');
    setVariety('');
    setSeasonYear('');
    setYieldRating(null);
    setTasteRating(null);
    setResistanceRating(null);
    setNote('');
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setPlantName(item.plant_name || '');
    setVariety(item.variety || '');
    setSeasonYear(item.season_year || '');
    setYieldRating(item.yield_rating);
    setTasteRating(item.taste_rating);
    setResistanceRating(item.resistance_rating);
    setNote(item.note || '');
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const ok = await confirmDialog({
      title: 'Usuń ocenę',
      message: 'Czy na pewno chcesz usunąć tę ocenę?',
      confirmText: 'Usuń'
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/variety-reviews/${id}`);
      notifySuccess('Usunięto ocenę');
      refetch();
    } catch (e) {
      notifyError('Nie udało się usunąć oceny');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!plant_name.trim()) {
      notifyError('Nazwa rośliny jest wymagana');
      return;
    }
    const payload = {
      plant_name,
      variety: variety || null,
      season_year: season_year ? Number(season_year) : null,
      yield_rating: yield_rating || null,
      taste_rating: taste_rating || null,
      resistance_rating: resistance_rating || null,
      note: note || null
    };
    try {
      if (editingId) {
        await axios.put(`/api/variety-reviews/${editingId}`, payload);
        notifySuccess('Zaktualizowano ocenę');
      } else {
        await axios.post('/api/variety-reviews', payload);
        notifySuccess('Dodano ocenę');
      }
      refetch();
      resetForm();
    } catch (e) {
      notifyError('Nie udało się zapisać oceny');
    }
  };

  const RatingInput = ({ label, value, onChange }) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontFamily: 'var(--t-mono)', fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--t-text-dim)', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? null : n)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid var(--t-line)',
              backgroundColor: value === n ? 'var(--t-amber)' : 'transparent',
              color: value === n ? '#fff' : 'var(--t-text-dim)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  const layoutProps = {
    topBar: <TopBar left={<Meta>Moje odmiany</Meta>} />
  };

  if (loading) return <Layout {...layoutProps}><LoadingState/></Layout>;
  if (error) return <Layout {...layoutProps}><ErrorState/></Layout>;

  return (
    <Layout {...layoutProps}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 60px' }}>
        <div style={{ marginBottom: 32 }}>
          <Meta color="sage">Sprawdzone u mnie</Meta>
          <Display size={30} style={{ display: 'block', marginTop: 6 }}>Moje odmiany</Display>
        </div>

        <div style={{ border: '1px solid var(--t-line)', borderRadius: '12px', padding: '20px', marginBottom: '24px', backgroundColor: 'var(--t-bg)' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontFamily: 'var(--t-mono)', fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--t-text-dim)' }}>
                Roślina *
              </label>
              <input
                type="text"
                aria-label="Roślina"
                value={plant_name}
                onChange={(e) => setPlantName(e.target.value)}
                placeholder="Np. Pomidor"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--t-line)', backgroundColor: 'var(--t-bg)', color: 'var(--t-text)', fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontFamily: 'var(--t-mono)', fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--t-text-dim)' }}>
                  Odmiana
                </label>
                <input
                  type="text"
                  aria-label="Odmiana"
                  value={variety}
                  onChange={(e) => setVariety(e.target.value)}
                  placeholder="Np. Malinowy"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--t-line)', backgroundColor: 'var(--t-bg)', color: 'var(--t-text)', fontFamily: 'inherit', fontSize: '14px' }}
                />
              </div>
              <div style={{ width: 120 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontFamily: 'var(--t-mono)', fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--t-text-dim)' }}>
                  Rok
                </label>
                <input
                  type="number"
                  aria-label="Rok sezonu"
                  value={season_year}
                  onChange={(e) => setSeasonYear(e.target.value)}
                  placeholder={currentYear}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--t-line)', backgroundColor: 'var(--t-bg)', color: 'var(--t-text)', fontFamily: 'inherit', fontSize: '14px' }}
                />
              </div>
            </div>

            <RatingInput label="Plon" value={yield_rating} onChange={setYieldRating} />
            <RatingInput label="Smak" value={taste_rating} onChange={setTasteRating} />
            <RatingInput label="Odporność" value={resistance_rating} onChange={setResistanceRating} />

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontFamily: 'var(--t-mono)', fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--t-text-dim)' }}>
                Notatka
              </label>
              <input
                type="text"
                aria-label="Notatka"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Opis doświadczeń..."
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--t-line)', backgroundColor: 'var(--t-bg)', color: 'var(--t-text)', fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button type="submit" variant="primary">{editingId ? 'Zapisz' : 'Dodaj ocenę'}</Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={resetForm}>Anuluj</Button>
              )}
            </div>
          </form>
        </div>

        {reviews.length === 0 ? (
          <EmptyState title="Brak ocen" description="Po zbiorze oceń odmianę — z każdym sezonem ta baza jest cenniejsza." />
        ) : (
          <div>
            {reviews.map((item) => (
              <div key={item.id} style={{ border: '1px solid var(--t-line)', borderRadius: '12px', padding: '16px', marginBottom: '12px', backgroundColor: 'var(--t-bg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--t-text)', marginBottom: '4px' }}>{item.plant_name}</div>
                    {item.variety && <Meta>{item.variety}</Meta>}
                  </div>
                  {item.season_year && <Chip tone="dim">{item.season_year}</Chip>}
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '14px', color: 'var(--t-text-dim)' }}>
                  {item.yield_rating && <span>Plon {item.yield_rating}/5</span>}
                  {item.taste_rating && <span>Smak {item.taste_rating}/5</span>}
                  {item.resistance_rating && <span>Odporność {item.resistance_rating}/5</span>}
                </div>

                {item.note && (
                  <div style={{ fontSize: '14px', color: 'var(--t-text-dim)', marginBottom: '12px', lineHeight: 1.5 }}>
                    {item.note}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--t-line)' }}>
                  <Button variant="ghost" onClick={() => handleEdit(item)}>Edytuj</Button>
                  <Button variant="ghost" onClick={() => handleDelete(item.id)}>Usuń</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyVarieties;